import React, { FC, useEffect, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import Text from '@ingka/text';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../store';
import {
  fetchFinancialFrames,
  updateFinancialFrame,
} from '../../store/financialFrames/actionWrappers';
import {
  selectCountryFinancialFrames,
  selectFinancialFrames,
  selectGlobalFinancialFrames,
  selectFinancialFramesLoading,
  selectFinancialFramesLoaded,
  selectFinancialFrameUpdateLoading,
} from '../../store/financialFrames/selectors';
import {
  FinancialFrame,
  FinancialFrameUpdate,
} from '../../store/financialFrames/stateType';
import FinancialFrameTable, {
  FinancialFrameGlobalTable,
} from '../../components/FinancialFrameTable';
import RmppTotalModal from '../../components/RmppTotalModal';
import FinancialFrameSideSheet from '../../components/FinancialFrameSideSheet';
import LoadingScreen from '../../common/LoadingScreen';
import EmptyState from '../../common/EmptyState';
import allenKey from '@ingka/ssr-icon/paths/allen-key';
import useTranslation from '../../hooks/useTranslation';
import { selectUserRole } from '../../store/userData/selectors';
import { roleRestricted, SA } from '../../components/RoleRestricted/utils';
import {
  selectFinancialFrameCountryOptions,
  selectedDataLoadingSelector,
} from '../../store/selectData/selectors';

const FY_OPTIONS = ['FY27', 'FY28', 'FY29', 'FY30'];

const FinancialFrames: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslation();
  const role = useTypedSelector(selectUserRole);
  const financialFrames = useTypedSelector(selectFinancialFrames);
  const countryFrames = useTypedSelector(selectCountryFinancialFrames);
  const globalFrames = useTypedSelector(selectGlobalFinancialFrames);
  const loading = useTypedSelector(selectFinancialFramesLoading);
  const loaded = useTypedSelector(selectFinancialFramesLoaded);
  const updateLoading = useTypedSelector(selectFinancialFrameUpdateLoading);
  const selectDataLoading = useTypedSelector(selectedDataLoadingSelector);
  const countryOptions = useTypedSelector(selectFinancialFrameCountryOptions);
  const [selectedFrame, setSelectedFrame] = useState<FinancialFrame | null>(
    null,
  );
  const [selectedFYs, setSelectedFYs] = useState<string[]>([FY_OPTIONS[0]]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [rmppTotal, setRmppTotal] = useState<{
    fiscalYear: string;
    group: string;
  } | null>(null);

  const fyOptions = FY_OPTIONS.map((fy) => ({ value: fy, label: fy }));
  useEffect(() => {
    dispatch(fetchFinancialFrames());
  }, []);

  const onRowClick = (id: number) => {
    const frame = countryFrames.find((item) => item.id === id);
    if (frame) {
      setSelectedFrame(frame);
    }
  };

  const onGlobalRowClick = (frame: FinancialFrame) => setSelectedFrame(frame);

  const onGlobalValueCellClick = (frame: FinancialFrame, group: string) =>
    setRmppTotal({ fiscalYear: frame.fiscalYear, group });

  const onFieldChange = (field: keyof FinancialFrameUpdate, value: string) => {
    setSelectedFrame((prev) =>
      prev ? { ...prev, [field]: Number(value) || 0 } : prev,
    );
  };

  const onClose = () => setSelectedFrame(null);

  const onUpdate = (id: number, body: FinancialFrameUpdate) => {
    dispatch(updateFinancialFrame({ id, body }));
  };

  // Close the modal once the update finishes (updateLoading goes true -> false).
  const wasUpdating = useRef(false);
  useEffect(() => {
    if (wasUpdating.current && !updateLoading) {
      setSelectedFrame(null);
    }
    wasUpdating.current = updateLoading;
  }, [updateLoading]);

  if (loading || selectDataLoading || !loaded) {
    return <LoadingScreen message={t('common.loadingMsg.loading')} />;
  }

  return (
    <Stack sx={{ width: '100%' }} px={2} pt={4} spacing={2}>
      <Text textStyle={'Heading.L'}>
        {t('appBar.navigation.financialFramesTab')}
      </Text>
      {financialFrames.length === 0 ? (
        <Stack sx={{ height: 'calc(100vh - 180px)' }}>
          <EmptyState
            icon={allenKey}
            message={t('financialFrames.emptyState.message')}
          />
        </Stack>
      ) : (
        <>
          <FinancialFrameTable
            financialFrames={countryFrames}
            onRowClick={onRowClick}
            editAllowed={roleRestricted(SA, role)}
            fyOptions={fyOptions}
            countryOptions={countryOptions}
            selectedFYs={selectedFYs}
            selectedCountries={selectedCountries}
            onFyChange={setSelectedFYs}
            onCountryChange={setSelectedCountries}
          />
          {globalFrames.length > 0 && (
            <Stack spacing={1} pb={2}>
              <Text textStyle={'Heading.S'}>{t('financialFrames.global')}</Text>
              <FinancialFrameGlobalTable
                financialFrames={globalFrames}
                selectedFYs={selectedFYs}
                editAllowed={roleRestricted(SA, role)}
                onRowClick={onGlobalRowClick}
                onValueCellClick={onGlobalValueCellClick}
              />
            </Stack>
          )}
          <FinancialFrameSideSheet
            frame={selectedFrame}
            loading={updateLoading}
            onFieldChange={onFieldChange}
            onClose={onClose}
            onUpdate={onUpdate}
          />
          <RmppTotalModal
            isVisible={!!rmppTotal}
            fiscalYear={rmppTotal?.fiscalYear ?? null}
            initialGroup={rmppTotal?.group ?? null}
            onClose={() => setRmppTotal(null)}
          />
        </>
      )}
    </Stack>
  );
};

export default FinancialFrames;
